import React, { useState, useRef, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {ChevronDown, Crown, Shield, Briefcase, Users, User} from 'lucide-react'
import { lumi } from "../lib/lumi"
import { isSuperadmin } from "../config/roles"

interface DashboardOption {
  id: string
  label: string
  path: string
  icon: React.ComponentType<any>
}

const dashboards: DashboardOption[] = [
  { id: "superadmin", label: "Superadmin", path: "/dashboard/superadmin", icon: Crown },
  { id: "admin", label: "Admin", path: "/dashboard/admin", icon: Shield },
  { id: "recruiter", label: "Recruiter", path: "/dashboard/recruiter", icon: Briefcase },
  { id: "affiliate", label: "Affiliate", path: "/dashboard/affiliate", icon: Users },
  { id: "nurse", label: "Nurse", path: "/dashboard/nurse", icon: User }
]

export default function DashboardSwitcher() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Only show for superadmins
  const user = lumi.auth.user
  if (!user?.email || !isSuperadmin(user.email)) {
    return null
  }

  // Determine current dashboard
  const currentDashboard = dashboards.find(d => location.pathname.startsWith(d.path))
  const CurrentIcon = currentDashboard?.icon || Crown

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSwitch = (path: string) => {
    setIsOpen(false)
    navigate(path)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-900 rounded-lg hover:bg-amber-200 transition-colors font-medium"
      >
        <CurrentIcon className="w-4 h-4" />
        <span className="hidden sm:inline">{currentDashboard?.label || "Switch Dashboard"}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
              Superadmin View
            </p>
          </div>
          {dashboards.map((dashboard) => {
            const Icon = dashboard.icon
            const isCurrent = location.pathname.startsWith(dashboard.path)
            return (
              <button
                key={dashboard.id}
                onClick={() => handleSwitch(dashboard.path)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors ${
                  isCurrent ? "bg-amber-50 text-amber-900 font-semibold" : "text-gray-700"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{dashboard.label}</span>
                {isCurrent && (
                  <span className="ml-auto text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">
                    Current
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
